import { FC, useCallback, useEffect, useState } from 'react'

import { useRouter } from '@tanstack/react-router'
import { PageSectionPrimary } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { TextareaInput } from 'ui/components/InputV2'
import { LeaveConfirmation } from 'ui/components/LeaveConfirmation'
import { Toast } from 'ui/components/Toast'

import { useAddNotes } from '../../../../hooks/useAddNotes'
import { ContactWithTheApplicantProps } from './ContactWithTheApplicantProps'

import * as SC from './styles'

export const ContactWithTheApplicant: FC<ContactWithTheApplicantProps> = ({ application }) => {
  const {
    notesForApplicant,
    notesFromApplicant,
    notesBankInternal,
    enableNotesForApplicantModification,
    enableInternalNotesModification,
    applicationID
  } = application

  const router = useRouter()
  const { addNotes } = useAddNotes()
  const [notesForTheApplicant, setNotesForTheApplicant] = useState<string>(notesForApplicant)
  const [internalNotes, setInternalNotes] = useState<string>(notesBankInternal)

  const isNotesSaved = notesForTheApplicant === notesForApplicant
  const isInternalNotesSaved = internalNotes === notesBankInternal

  const onChangeNotesForTheApplicant = useCallback(
    (value: string) => {
      setNotesForTheApplicant(value)
    },
    [setNotesForTheApplicant]
  )

  const onChangeInternalNotes = useCallback(
    (value: string) => {
      setInternalNotes(value)
    },
    [setInternalNotes]
  )

  const onSaveNotes = useCallback(
    async (saveInternal: boolean) => {
      const body = { id: applicationID }

      if (saveInternal) {
        body['internalBankNotes'] = internalNotes
      } else {
        body['bankNotes'] = notesForTheApplicant
      }
      try {
        await addNotes(body)
        Toast.success(saveInternal ? 'Internal Bank Notes has been saved.' : 'Bank Notes has been saved.')
        router.invalidate()
      } catch (error) {
        Toast.error(saveInternal ? 'Cannot save internal bank notes.' : 'Cannot save bank notes.')
      }
    },
    [addNotes, applicationID, internalNotes, notesForTheApplicant, router]
  )

  useEffect(() => {
    setInternalNotes(notesBankInternal)
  }, [notesBankInternal])

  return (
    <LeaveConfirmation preventLeaving={!isInternalNotesSaved || !isNotesSaved}>
      <PageSectionPrimary title="Contact with the Applicant" face="solid" anchor="contact">
        <SC.Label>Applicant&apos;s notes</SC.Label>
        <SC.ClientNotes>{notesFromApplicant || 'No notes.'}</SC.ClientNotes>
        <SC.CustomHr />
        <SC.Label>Notes for the Applicant</SC.Label>
        <SC.NotesWrapper>
          {!enableNotesForApplicantModification && (
            <SC.ClientNotes>{notesForTheApplicant || 'No Notes'}</SC.ClientNotes>
          )}
          {enableNotesForApplicantModification && (
            <TextareaInput
              value={notesForTheApplicant}
              onChange={onChangeNotesForTheApplicant}
              disabled={!enableNotesForApplicantModification}
            />
          )}
        </SC.NotesWrapper>

        {enableNotesForApplicantModification && (
          <Button
            face="primary"
            disabled={isNotesSaved || !enableNotesForApplicantModification}
            onClick={() => onSaveNotes(false)}
          >
            Save
          </Button>
        )}
        <SC.CustomHr $marginTop={true} />
        <SC.Label>Internal Notes (only visible to Bank)</SC.Label>
        <SC.NotesWrapper>
          {!enableInternalNotesModification && <SC.ClientNotes>{internalNotes || 'No Notes'}</SC.ClientNotes>}
          {enableInternalNotesModification && <TextareaInput value={internalNotes} onChange={onChangeInternalNotes} />}
        </SC.NotesWrapper>
        {enableInternalNotesModification && (
          <Button face="primary" disabled={isInternalNotesSaved} onClick={() => onSaveNotes(true)}>
            Save
          </Button>
        )}
      </PageSectionPrimary>
    </LeaveConfirmation>
  )
}
